/* eslint-disable react/no-danger */
import * as React from 'react';
import classnames from 'classnames';
import { createRecordUrl, formatMoney, formatPercent, ViewType } from 'utils/formatter';
import { formStyles } from 'styles/formStyles';
import moment from 'moment-timezone';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { betreuerByNrSelector } from 'store/lookups/selector';
import { authSelector } from 'store/auth/selector';
import { useSelector } from 'react-redux';
import { FilterValues, LineData, UmsatzSummaryPosition } from './types';
import { UmsatzSummaryProps, useUmsatzSummary } from './hooks/useUmsatzSummary';

import { styles } from './Umsatzuebersicht';

interface UmsatzuebersichtListProps extends FilterValues {
    printView?: boolean;
}

export const UmsatzuebersichtList: React.FC<UmsatzuebersichtListProps> = (filterValues) => {
    const auth = useSelector(authSelector);
    const betreuerByNr = useSelector(betreuerByNrSelector);

    const { source, printView } = filterValues;

    const umsatzSummaryProps = React.useMemo<UmsatzSummaryProps>(
        () => ({
            ...filterValues,
            enabled: true,
        }),
        [filterValues],
    );

    const umsatzSummary = useUmsatzSummary(umsatzSummaryProps);

    const renderHeaderRow = () => {
        const moneyClassName = classnames(
            styles.tdHead,
            formStyles.dataValueRight,
            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
        );

        const percentClassName = classnames(
            styles.tdHead,
            formStyles.dataValueRight,
            printView ? formStyles.reportPercentPrintCell : formStyles.reportPercentCell,
        );

        return (
            <tr>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportAuNrPrintCell : formStyles.reportAuNrCell,
                    )}
                >
                    Nr.
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                    )}
                >
                    Datum
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportKundePrintCell : formStyles.reportKundeCell,
                    )}
                >
                    Kunde / Artikel-Nr.
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportBetreuerKz2PrintCell : formStyles.reportBetreuerKz2Cell,
                    )}
                >
                    B./Tel.B.
                </td>
                <td className={classnames(percentClassName, styles.ekNetto)} />
                <td className={classnames(moneyClassName, styles.ekNetto)}>EK Netto</td>
                <td className={classnames(percentClassName, styles.vkNetto)} />
                <td className={classnames(moneyClassName, styles.vkNetto)}>VK Netto</td>
                <td className={classnames(percentClassName, styles.bMarge)} />
                <td className={classnames(moneyClassName, styles.bMarge, styles.borderRight)}>B-Marge</td>
                <td className={classnames(percentClassName, styles.nMarge)} />
                <td className={classnames(moneyClassName, styles.nMarge, styles.borderRight)}>N-Marge</td>
                <td className={classnames(percentClassName, styles.bAnteil)} />
                <td className={classnames(moneyClassName, styles.bAnteil)}>B-Anteil</td>
                {auth.rolle !== 'AD' && (
                    <>
                        <td className={classnames(percentClassName, styles.fAnteil)} />
                        <td className={classnames(moneyClassName, styles.fAnteil)}>F-Anteil</td>
                    </>
                )}
            </tr>
        );
    };

    const moneyClassName = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
    );
    const percentClassName = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportPercentPrintCell : formStyles.reportPercentCell,
    );

    const renderMoney = (value: number, className: string, borderRight?: boolean) => {
        return (
            <>
                <td className={classnames(percentClassName, className)} />
                <td
                    className={classnames(moneyClassName, className, {
                        [styles.borderRight]: borderRight,
                    })}
                >
                    {formatMoney(value)}
                </td>
            </>
        );
    };

    const renderSummaryLine = (title: string, line: LineData) => {
        return (
            <tr>
                <td colSpan={4}>{title}</td>
                {renderMoney(line.ekNetto, styles.ekNetto)}
                {renderMoney(line.vkNetto, styles.vkNetto)}
                {renderMoney(line.bMarge, styles.bMarge, true)}
                {renderMoney(line.nMarge, styles.nMarge, true)}
                {renderMoney(line.bAnteil, styles.bAnteil)}
                {auth.rolle !== 'AD' && renderMoney(line.fAnteil, styles.fAnteil)}
            </tr>
        );
    };

    const renderPosition = (position: UmsatzSummaryPosition) => {
        const recordUrl = createRecordUrl(
            ({ auftrag: 'auftraege', rechnung: 'rechnungen', bestellung: 'bestellungen' }[source] as ViewType) ||
                'auftraege',
            position.kNr,
            position.recordNr,
        );

        return (
            <tr key={position.recordNr + '.' + position.pos}>
                <td
                    className={classnames(
                        styles.tdBody,
                        printView ? formStyles.reportAuNrPrintCell : formStyles.reportAuNrCell,
                    )}
                >
                    {printView ? position.recordNr : <OpenLink link={recordUrl} label={position.recordNr} />}
                    {/* position.betrUm ? '*' : '' */}
                </td>
                <td
                    className={classnames(
                        styles.tdBody,
                        printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                    )}
                >
                    {moment(position.datum).format('DD.MM.YYYY')}
                </td>
                <td
                    className={classnames(
                        styles.tdBody,
                        printView ? formStyles.reportKundePrintCell : formStyles.reportKundeCell,
                    )}
                >
                    {position.kName}
                    <br />
                    <strong title={position.posText}>{position.artikelNr}</strong>
                </td>
                <td
                    className={classnames(
                        styles.tdBody,
                        printView ? formStyles.reportBetreuerKz2PrintCell : formStyles.reportBetreuerKz2Cell,
                    )}
                >
                    {betreuerByNr[position.betreuer]?.kz || '?'}
                    {' / '}
                    {betreuerByNr[position.telBetreuer]?.kz || '?'}
                </td>
                {renderMoney(position.lineData?.ekNetto || 0, styles.ekNetto)}
                {renderMoney(position.lineData?.vkNetto || 0, styles.vkNetto)}
                {renderMoney(position.lineData?.bMarge || 0, styles.bMarge, true)}
                {renderMoney(position.lineData?.nMarge || 0, styles.nMarge, true)}
                {renderMoney(position.lineData?.bAnteil || 0, styles.bAnteil)}
                {auth.rolle !== 'AD' && renderMoney(position.lineData?.fAnteil || 0, styles.fAnteil)}
            </tr>
        );
    };

    return (
        <div className={printView ? styles.printContainer : styles.container}>
            {umsatzSummary.loading && <LoadingIndicator />}
            {printView && (
                <div
                    dangerouslySetInnerHTML={{
                        __html:
                            // eslint-disable-next-line max-len
                            '<style>html, body, #root { overflow: unset !important; height: unset !important; }</style>',
                    }}
                />
            )}
            {!printView && <table className={formStyles.reportFixedHeaderNoBgColor}>{renderHeaderRow()}</table>}
            <h1>Umsatzübersicht - Details</h1>
            <table className={formStyles.reportTable}>
                <thead>{renderHeaderRow()}</thead>
                <tbody>
                    {(umsatzSummary.recordPositionList || []).map((position) => renderPosition(position))}

                    <tr>
                        <td colSpan={22} className={classnames(styles.strongBorderBottom, styles.noPadding)} />
                    </tr>

                    {renderSummaryLine(
                        'Gesamt (' + (umsatzSummary ? umsatzSummary.total.count : '...') + ')',
                        umsatzSummary.total,
                    )}
                    <tr>
                        <td colSpan={4}>%-Anteil</td>
                        <td className={classnames(percentClassName, styles.ekNetto)} />
                        <td className={classnames(percentClassName, styles.ekNetto)} />
                        <td className={classnames(percentClassName, styles.vkNetto)} />
                        <td className={classnames(percentClassName, styles.vkNetto)} />

                        <td className={classnames(percentClassName, styles.bMarge)} />
                        <td className={classnames(moneyClassName, styles.bMarge)}>
                            {formatPercent(umsatzSummary.total.bMarge / (umsatzSummary.total.vkNetto / 100), 2)}
                        </td>
                        <td className={classnames(percentClassName, styles.nMarge)} />
                        <td className={classnames(moneyClassName, styles.nMarge, styles.borderRight)}>
                            {formatPercent(umsatzSummary.total.nMarge / (umsatzSummary.total.vkNetto / 100), 2)}
                        </td>
                        <td className={classnames(percentClassName, styles.bAnteil)} />
                        <td className={classnames(moneyClassName, styles.bAnteil)}>
                            {formatPercent(umsatzSummary.total.bAnteil / (umsatzSummary.total.vkNetto / 100), 2)}
                        </td>
                        {auth.rolle !== 'AD' && (
                            <>
                                <td className={classnames(percentClassName, styles.fAnteil)} />
                                <td className={classnames(moneyClassName, styles.fAnteil)}>
                                    {formatPercent(
                                        umsatzSummary.total.fAnteil / (umsatzSummary.total.vkNetto / 100),
                                        2,
                                    )}
                                </td>
                            </>
                        )}
                    </tr>
                    <tr>
                        <td colSpan={22} className={classnames(styles.strongBorderBottom, styles.noPadding)} />
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
